/**
 * The daily premium — R30 a day, and what has been paid against it.
 *
 * Deliberately not `billingRepository`. That one talks about the shop's monthly
 * package and its invoices; this one talks about the daily charge and the
 * ledger behind it. They sit under the same URL prefix and are the same screen
 * to a shopkeeper, but they are different documents with different rules, and
 * one repository holding both would be one type union away from a component
 * showing an invoice's status vocabulary on a ledger row.
 *
 * **Money arrives twice and is used once.** Every amount comes as `*_cents` and
 * as a decimal string. The string is what gets formatted — `formatMoney` takes
 * decimals, and dividing cents by a hundred in JavaScript is the float the
 * backend's whole money layer exists to avoid. The cents are for comparisons:
 * `amount_due_cents > 0` is exact, `Number("30.00") > 0` is a parse.
 */

import { api } from "@/api/client";

import type { Page } from "./adminRepository";

// ═══════════════════════════════════════════════════════════════════════════════
// Shapes
// ═══════════════════════════════════════════════════════════════════════════════

/** §3.1's six. Derived on the server from the ledger, never stored. */
export type PremiumStatus =
  | "PAID"
  | "DUE_TODAY"
  | "OVERDUE"
  | "PAYMENT_PENDING"
  | "SUSPENDED"
  | "INACTIVE";

export type LedgerEntryType =
  | "DAILY_PREMIUM"
  | "PAYMENT"
  | "CREDIT_ADJUSTMENT"
  | "DEBIT_ADJUSTMENT"
  | "REFUND"
  | "REVERSAL";

export type PaymentChannel = "CARD" | "EFT" | "DEBIT_ORDER";

export type IntentStatus =
  "PENDING" | "SUCCEEDED" | "FAILED" | "EXPIRED" | "CANCELLED";

export interface LedgerEntry {
  id: string;
  entry_type: LedgerEntryType;
  entry_type_display: string;
  direction: "DEBIT" | "CREDIT";
  status: "POSTED" | "REVERSED";
  service_date: string;
  posted_at: string;
  amount_cents: number;
  amount: string;
  description: string;
  external_reference: string;
  related_entry: string | null;
}

export interface PaymentIntent {
  id: string;
  status: IntentStatus;
  amount_cents: number;
  amount: string;
  currency: string;
  method: PaymentChannel;
  provider: string;
  provider_reference: string;
  checkout_url: string;
  failure_reason: string;
  expires_at: string;
  created_at: string;
}

export interface SavedPaymentMethod {
  id: string;
  method_type: PaymentChannel;
  masked_label: string;
  mandate_status: "NONE" | "PENDING" | "ACTIVE" | "CANCELLED";
  mandate_reference: string;
  is_default: boolean;
  can_collect: boolean;
  created_at: string;
}

export interface PremiumAccount {
  id: string;
  state: "ACTIVE" | "SUSPENDED" | "CLOSED";
  daily_rate_cents: number;
  daily_rate: string;
  grace_days: number;
  currency: string;
  billing_timezone: string;
  active_from: string;
  active_to: string | null;
  suspended_at: string | null;
  suspension_reason: string;
}

/** §2's dashboard and §6.1's response, in one object. */
export interface PremiumOverview {
  merchant_id: string;
  merchant_number: string;
  status: PremiumStatus;
  currency: string;

  daily_rate_cents: number;
  amount_due_cents: number;
  amount_due: string;
  available_credit_cents: number;
  available_credit: string;

  paid_through: string | null;
  oldest_unpaid_date: string | null;
  next_charge_date: string | null;
  grace_days_remaining: number;
  today: string;

  can_pay: boolean;
  payment_methods: PaymentChannel[];

  account: PremiumAccount;
  recent_entries: LedgerEntry[];
  pending_intent: PaymentIntent | null;
}

export interface StatementLine {
  service_date: string;
  posted_at: string;
  description: string;
  reference: string;
  debit_cents: number;
  credit_cents: number;
  balance_cents: number;
  status: "POSTED" | "REVERSED";
}

export interface Statement {
  from_date: string;
  to_date: string;
  opening_cents: number;
  closing_cents: number;
  total_debits_cents: number;
  total_credits_cents: number;
  /** §AC-07: opening + debits − credits = closing. False is a server bug. */
  balances: boolean;
  lines: StatementLine[];
}

export interface LedgerFilters {
  type?: LedgerEntryType;
  from?: string;
  to?: string;
  cursor?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Calls
// ═══════════════════════════════════════════════════════════════════════════════
export const premiumBillingRepository = {
  overview: () => api.get<PremiumOverview>("/billing/me/"),

  ledger: (filters: LedgerFilters = {}) => {
    const query = new URLSearchParams();
    if (filters.type) query.set("type", filters.type);
    if (filters.from) query.set("from", filters.from);
    if (filters.to) query.set("to", filters.to);
    if (filters.cursor) query.set("cursor", filters.cursor);

    const suffix = query.toString();
    return api.get<Page<LedgerEntry>>(
      `/billing/me/ledger/${suffix ? `?${suffix}` : ""}`,
    );
  },

  /**
   * Start a payment.
   *
   * The idempotency key is the caller's, and it must be **stable across
   * retries of one intention** — generated when the dialog opens, not when the
   * request is sent. A key generated per request would make every retry on a
   * slow connection a second checkout, which is the exact failure the header
   * exists to prevent.
   *
   * Note what is not sent: the balance. The server holds it (§4.1 step 2), and
   * an amount above it is an advance payment rather than a correction.
   */
  startPayment: (
    input: {
      amount_cents: number;
      method: PaymentChannel;
      payment_method_id?: string;
    },
    idempotencyKey: string,
  ) =>
    api.post<PaymentIntent>("/billing/me/payment-intents/", input, {
      idempotencyKey,
    }),

  paymentIntent: (id: string) =>
    api.get<PaymentIntent>(`/billing/me/payment-intents/${id}/`),

  cancelPayment: (id: string) =>
    api.delete<void>(`/billing/me/payment-intents/${id}/`),

  paymentMethods: () =>
    api.get<Page<SavedPaymentMethod>>("/billing/me/payment-methods/"),

  removePaymentMethod: (id: string) =>
    api.delete<void>(`/billing/me/payment-methods/${id}/`),

  /** The current month by default; a custom range with `from` and `to`. */
  statement: (range: { from?: string; to?: string } = {}) => {
    const query = new URLSearchParams();
    if (range.from) query.set("from", range.from);
    if (range.to) query.set("to", range.to);

    const suffix = query.toString();
    return api.get<Statement>(
      `/billing/me/statements/${suffix ? `?${suffix}` : ""}`,
    );
  },

  /**
   * Download a statement.
   *
   * A POST that returns a file, which is unusual — but the range is a pair of
   * dates and a format, and a GET that produced a PDF from query parameters
   * would be a URL somebody pastes into a chat and expects to keep working
   * after the ledger has moved on.
   */
  downloadStatement: (input: {
    from_date: string;
    to_date: string;
    format: "pdf" | "csv";
  }) =>
    api.post<Blob>("/billing/me/statements/", input, { responseType: "blob" }),
};
